import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { debounce } from 'lodash';
import axios from 'axios';
import { useAgentDetails } from '../utils/useAgentDetails';
import EnquiryModal from '../components/EnquiryModal';
import SuburbSearchDesktop from './SuburbSearchDesktop';
import SuburbSearchMobile from './SuburbSearchMobile';
import './PropertySearchLive.css';

const MOBILE_BREAKPOINT = 768;

// Group label shown inside the react-select dropdown ("Postcode" / "Suburb"
// section headers, each with a count badge).
const formatGroupLabel = (data) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <span style={{
      fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase',
      letterSpacing: '0.08em', color: '#9ca3af',
    }}>{data.label}</span>
    <span style={{
      backgroundColor: '#f3f4f6', borderRadius: '2em', color: '#6b7280',
      display: 'inline-block', fontSize: 11, fontWeight: 600,
      lineHeight: '1', minWidth: 1, padding: '3px 8px', textAlign: 'center',
    }}>{data.options.length}</span>
  </div>
);

// Split the flat API response into Postcode / Suburb option groups.
const groupResults = (results) => {
  const suburbOptions = results.filter((r) => r.type === 'suburb');
  const postcodeOptions = results.filter((r) => r.type === 'postcode');
  const groups = [];
  if (postcodeOptions.length > 0) {
    groups.push({
      label: 'Postcode',
      options: postcodeOptions.map((opt) => ({
        ...opt,
        label: `${opt.value}, ${opt.state || ''}`.replace(/,\s*$/, ''),
      })),
    });
  }
  if (suburbOptions.length > 0) {
    groups.push({
      label: 'Suburb',
      options: suburbOptions.map((opt) => ({ ...opt, state: opt.state || '' })),
    });
  }
  return groups;
};

/**
 * Conversion-focused landing page: search a suburb/postcode → location report.
 * Shared data/search logic lives here; the actual layout is delegated to a
 * desktop- or mobile-optimised presentational component based on viewport,
 * mirroring the PropertySearchLive landing page.
 */
function SuburbSearch() {
  const { agentDetails, branding } = useAgentDetails();
  const navigate = useNavigate();

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (agentDetails) {
      document.title = `${agentDetails.agency_name_short} Location Report Search`;
    }
  }, [agentDetails]);

  // Clear any background image a previous page (e.g. AgentHome) set on <body>.
  useEffect(() => {
    document.body.style.backgroundImage = null;
    document.body.style.backgroundColor = null;
  }, []);

  const loadLocations = debounce((inputValue, callback) => {
    axios.get(`${window.api_host}/location-search?q=${inputValue}`)
      .then((response) => callback(groupResults(response.data)))
      .catch((error) => {
        console.error('Error loading location options:', error);
        callback([]);
      });
  }, 500);

  const goToLocation = (option) => {
    setIsSubmitting(true);
    const agentSlug = agentDetails.landing_page_slug;
    const base = option.type === 'postcode'
      ? `/postcode-profile/${option.value}`
      : `/suburb-profile/${option.value}`;
    navigate(agentSlug === 'home-price' ? base : `${base}?agent=${agentSlug}`);
  };

  const handleLocationChange = (option) => {
    setSelectedLocation(option);
    setMessage('');
    if (option) goToLocation(option);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!selectedLocation) {
      setMessage('Please select a suburb or postcode to view.');
      return;
    }
    goToLocation(selectedLocation);
  };

  if (!agentDetails) {
    return (
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
        <Spinner animation="border" />
      </div>
    );
  }

  const sharedProps = {
    agentDetails,
    branding,
    selectedLocation,
    loadLocations,
    handleLocationChange,
    handleSubmit,
    formatGroupLabel,
    message,
    isSubmitting,
    onContactAgent: () => setShowEnquiry(true),
  };

  return (
    <>
      {isMobile
        ? <SuburbSearchMobile {...sharedProps} />
        : <SuburbSearchDesktop {...sharedProps} />}
      <EnquiryModal
        show={showEnquiry}
        onHide={() => setShowEnquiry(false)}
        agentDetails={agentDetails}
      />
    </>
  );
}

export default SuburbSearch;
